import React, { useState, useRef, useEffect } from 'react';

export interface BulletPointTextareaProps {
  value?: string[]; // array of bullet strings
  onChange?: (bullets: string[]) => void;
  placeholder?: string;
  label?: string;
}

const BulletPointTextarea: React.FC<BulletPointTextareaProps> = ({
  value = [],
  onChange,
  placeholder = 'Click to start typing with bullet points...',
  label = ''
}) => {
  const [internalValue, setInternalValue] = useState<string>('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /** Convert array -> textarea string */
  const formatValue = (items: string[]) =>
    items.length === 0 ? '' : items.map((line) => `• ${line}`).join('\n');

  /** Parse textarea string -> array */
  const parseValue = (text: string) =>
    text
      .split('\n')
      .map((line) => line.replace(/^•\s*/, '').trim())
      .filter((line) => line !== '');

  /** Prefill only once (or when parent value explicitly changes while empty) */
  useEffect(() => {
    if (value.length > 0 && internalValue === '') {
      setInternalValue(formatValue(value));
    }
  }, [value]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setInternalValue(newText);
    onChange?.(parseValue(newText));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const cursor = e.currentTarget.selectionStart;
      const before = internalValue.substring(0, cursor);
      const after = internalValue.substring(cursor);

      // detect empty bullet -> exit bullet mode
      const lastLine = before.split('\n').pop() || '';
      if (lastLine.trim() === '•' || lastLine.trim() === '• ') {
        const newText = before.replace(/\s*•\s*$/, '') + '\n' + after;
        setInternalValue(newText);
        onChange?.(parseValue(newText));
        requestAnimationFrame(() => textareaRef.current?.setSelectionRange(cursor, cursor));
        return;
      }

      // otherwise insert new bullet
      const insertText = '\n• ';
      const newText = before + insertText + after;

      setInternalValue(newText);
      onChange?.(parseValue(newText));

      requestAnimationFrame(() => {
        const newCursor = cursor + insertText.length;
        textareaRef.current?.setSelectionRange(newCursor, newCursor);
      });
    }
  };

  const handleFocus = () => {
    if (internalValue === '') {
      const newText = '• ';
      setInternalValue(newText);
      requestAnimationFrame(() => textareaRef.current?.setSelectionRange(2, 2));
    }
  };

  return (
    <div className="flex flex-col w-full">
      {label && <p className="text-[12px] font-medium text-[#959595] mb-[8px]">{label}</p>}
      <textarea
        ref={textareaRef}
        value={internalValue}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={placeholder}
        className="overflow-scroll text-[#321A50] text-[15px] font-medium h-[100px] bg-white w-full p-4 rounded-lg shadow-sm border-2 border-[#E7E7E7] max-w-xl"
        style={{
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          lineHeight: '1.6'
        }}
      />
    </div>
  );
};

export default BulletPointTextarea;
